import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import type { XeroInvoiceDisplayStatus, XeroInvoiceRow } from '../../services/xeroApi';
import { fmtXeroMoney } from '../../services/xeroApi';

const STATUS_MAP: Record<
  XeroInvoiceDisplayStatus,
  { label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info' }
> = {
  paid: { label: 'Paid', color: 'success' },
  overdue: { label: 'Overdue', color: 'error' },
  outstanding: { label: 'Outstanding', color: 'warning' },
  draft: { label: 'Draft', color: 'default' },
  void: { label: 'Void', color: 'default' },
  other: { label: 'Other', color: 'info' },
};

export function XeroStatusChip({ row }: { row: Pick<XeroInvoiceRow, 'displayStatus' | 'status'> }) {
  const s = STATUS_MAP[row.displayStatus] || { label: row.status, color: 'info' as const };
  return <Chip size="small" label={s.label} color={s.color} variant="outlined" />;
}

export function XeroHero({ compact, title, subtitle }: { compact?: boolean; title?: string; subtitle?: string }) {
  return (
    <Box sx={compact ? undefined : { mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={compact ? 0 : 0.5}>
        <AccountBalanceOutlinedIcon sx={{ fontSize: compact ? 28 : 32, color: '#34d399' }} />
        <Typography variant={compact ? 'h5' : 'h4'} fontWeight={800}>
          {title || 'Xero'}
        </Typography>
      </Stack>
      {!compact && subtitle && (
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

export function XeroStatCard({
  label,
  value,
  sub,
  color,
  icon,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        height: '100%',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        </Box>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}22`, color }}>{icon}</Box>
      </Stack>
    </Box>
  );
}

export { fmtXeroMoney };
